import { ErroHttp, lerRetryAfterSegundos } from '@/shared/utils/erroHttp';

/**
 * Salva um Blob como arquivo local via âncora temporária, revogando o object URL
 * ao final. Fonte única do "download por blob" no repo.
 */
export const salvarBlobComoArquivo = (blob: Blob, nomeArquivo: string): void => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = nomeArquivo;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};

/**
 * Navega para uma URL já servida com `Content-Disposition: attachment`. É o único
 * caminho que baixa de fato numa URL cross-origin — o atributo `download` da âncora
 * é ignorado fora da mesma origem.
 */
export const triggerBrowserDownload = (url: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Busca os bytes de uma URL já autorizada por ela mesma (signed URL do GCS) e
 * devolve o Blob. `credentials: 'omit'` é obrigatório e não é zelo: o CORS do
 * bucket não emite `Access-Control-Allow-Credentials`, e um GET credenciado só é
 * legível se a resposta trouxer esse cabeçalho — o navegador descarta a resposta.
 * (Este GET é simples, então não há preflight: a rejeição acontece na resposta.)
 * `omit` derruba essa exigência, mas **não** o CORS em si: o bucket ainda precisa
 * devolver `Access-Control-Allow-Origin` para a origem do front. A autorização
 * mora na assinatura da URL, não no cookie (decisão D12, etapa 3b).
 *
 * Falha vira `ErroHttp` com o status preservado porque quem chama decide por
 * status: o lote do zip trata 429/503 com cooldown e só os demais como
 * "falha-download". Um `Error` genérico colapsaria os dois casos.
 */
export const buscarBytesRemotos = async (url: string, signal?: AbortSignal): Promise<Blob> => {
  const res = await fetch(url, { signal, credentials: 'omit' });
  if (!res.ok) {
    throw new ErroHttp(
      `Não foi possível buscar o arquivo (HTTP ${res.status})`,
      res.status,
      lerRetryAfterSegundos(res.headers),
    );
  }
  return res.blob();
};

/**
 * Baixa arquivo de origem externa buscando o conteúdo e salvando o blob. Resposta
 * não-ok do storage cai no fallback de abrir em nova aba, em vez de salvar o corpo
 * do erro com o nome do documento.
 */
export const baixarArquivoRemoto = async (url: string, nomeArquivo: string): Promise<void> => {
  try {
    salvarBlobComoArquivo(await buscarBytesRemotos(url), nomeArquivo);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

export const copyTextToClipboard = async (text: string): Promise<void> => {
  if (typeof window === 'undefined') {
    throw new Error('Clipboard indisponível');
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';

  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  const success = document.execCommand('copy');

  document.body.removeChild(textarea);

  if (!success) {
    throw new Error('Não foi possível copiar o texto');
  }
};

interface DownloadElementAsPdfParams {
  element: HTMLElement;
  fileName: string;
  backgroundColor?: string;
  scale?: number;
}

export const downloadElementAsPdf = async ({
  element,
  fileName,
  backgroundColor = '#ffffff',
  scale = 2,
}: DownloadElementAsPdfParams): Promise<void> => {
  const [{ domToCanvas }, { jsPDF }] = await Promise.all([
    import('modern-screenshot'),
    import('jspdf'),
  ]);

  const canvas = await domToCanvas(element, { scale, backgroundColor });

  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  let heightLeft = imgHeight;
  let position = 0;

  pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
  heightLeft -= pageHeight;

  while (heightLeft > 0) {
    position -= pageHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
    heightLeft -= pageHeight;
  }

  pdf.save(fileName);
};

interface DownloadElementsAsPdfParams {
  elements: HTMLElement[];
  fileName: string;
  backgroundColor?: string;
  scale?: number;
}

export const downloadElementsAsPdf = async ({
  elements,
  fileName,
  backgroundColor = '#ffffff',
  scale = 2,
}: DownloadElementsAsPdfParams): Promise<void> => {
  if (elements.length === 0) {
    throw new Error('Nenhuma página para gerar o PDF');
  }

  const [{ domToCanvas }, { jsPDF }] = await Promise.all([
    import('modern-screenshot'),
    import('jspdf'),
  ]);

  const pdf = new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  for (let index = 0; index < elements.length; index++) {
    const canvas = await domToCanvas(elements[index], { scale, backgroundColor });

    const imgData = canvas.toDataURL('image/png');
    const imgHeight = (canvas.height * pageWidth) / canvas.width;

    if (index > 0) {
      pdf.addPage();
    }

    pdf.addImage(
      imgData,
      'PNG',
      0,
      0,
      pageWidth,
      Math.min(imgHeight, pageHeight),
      undefined,
      'FAST',
    );
  }

  pdf.save(fileName);
};
